import type { Tone } from "../../shared/ui";
import type {
  BugSeverity,
  DeploymentStatus,
  ExecutionPhase,
  ExecutionStepStatus,
  OnFailureAction,
  QaDisposition,
  ReadinessStatus,
  ReleaseApprovalRole,
  ReleaseApprovalStatus,
  ReleaseGateStatus,
  ReleasePrerequisiteStatus,
  ReleaseQaStatus,
  ReleaseRelationType,
  ReleaseScopeChangeReason,
  ReleaseStatus,
  ReleaseTestStatus,
  ReleaseType,
  RollbackStepStatus,
  ValidationStatus,
} from "../../shared/types/api";

/**
 * How release vocabulary reads.
 *
 * <p>Kept in one place so a status looks the same on the list, the header and the history feed.
 * Two screens disagreeing about what "scope frozen" looks like is the sort of small inconsistency
 * that makes an application feel unfinished.
 */

export const RELEASE_STATUS_LABELS: Record<ReleaseStatus, string> = {
  DRAFT: "Draft",
  PLANNING: "Planning",
  SCOPE_FROZEN: "Scope frozen",
  VALIDATING: "Validating",
  READY: "Ready",
  APPROVED: "Approved",
  SCHEDULED: "Scheduled",
  DEPLOYING: "Deploying",
  DEPLOYED: "Deployed",
  VERIFYING: "Verifying",
  COMPLETED: "Completed",
  ON_HOLD: "On hold",
  FAILED: "Failed",
  ROLLED_BACK: "Rolled back",
  CANCELLED: "Cancelled",
  ARCHIVED: "Archived",
};

/**
 * Colour by what the state means, not by where it sits in the sequence.
 *
 * <p>DEPLOYED is blue rather than green on purpose: the steps finished, which is not the same as
 * the release having worked (§40). Green arrives at COMPLETED, once validation has actually said
 * so — anything earlier would be the screen congratulating itself.
 */
export const RELEASE_STATUS_TONE: Record<ReleaseStatus, Tone> = {
  DRAFT: "gray",
  PLANNING: "gray",
  SCOPE_FROZEN: "violet",
  VALIDATING: "amber",
  READY: "green",
  APPROVED: "green",
  SCHEDULED: "blue",
  DEPLOYING: "amber",
  DEPLOYED: "blue",
  VERIFYING: "amber",
  COMPLETED: "green",
  ON_HOLD: "amber",
  FAILED: "red",
  ROLLED_BACK: "red",
  CANCELLED: "gray",
  ARCHIVED: "gray",
};

export const RELEASE_TYPE_LABELS: Record<ReleaseType, string> = {
  MAJOR: "Major",
  MINOR: "Minor",
  PATCH: "Patch",
  HOTFIX: "Hotfix",
  EMERGENCY: "Emergency",
  UPGRADE: "Upgrade",
  INFRASTRUCTURE: "Infrastructure",
  CONFIGURATION: "Configuration",
};

export const RELEASE_TYPE_TONE: Record<ReleaseType, Tone> = {
  MAJOR: "violet",
  MINOR: "blue",
  PATCH: "gray",
  HOTFIX: "amber",
  EMERGENCY: "red",
  UPGRADE: "blue",
  INFRASTRUCTURE: "gray",
  CONFIGURATION: "gray",
};

export const READINESS_LABELS: Record<ReadinessStatus, string> = {
  NOT_EVALUATED: "Not checked yet",
  READY: "Ready for release",
  READY_WITH_WAIVER: "Released with waiver",
  NOT_READY: "Not ready",
};

export const READINESS_TONE: Record<ReadinessStatus, Tone> = {
  NOT_EVALUATED: "gray",
  READY: "green",
  /* Violet, not green. A release going out over a failed gate is not the same thing as a clean
     one, and the header is where that difference has to be visible without reading further. */
  READY_WITH_WAIVER: "violet",
  NOT_READY: "red",
};

export const GATE_STATUS_TONE: Record<ReleaseGateStatus, Tone> = {
  PASS: "green",
  FAIL: "red",
  WAITING: "amber",
  NOT_APPLICABLE: "gray",
};

export const RELATION_LABELS: Record<ReleaseRelationType, string> = {
  PRIMARY_TARGET: "Primary target",
  ADDITIONAL_TARGET: "Also shipping in",
  AFFECTED_RELEASE: "Affected",
  ACTUAL_RELEASE: "Shipped in",
};

export const QA_DISPOSITION_LABELS: Record<QaDisposition, string> = {
  REQUIRED: "Required",
  NOT_REQUIRED: "Not required",
  WAIVED: "Waived",
};

export const QA_STATUS_LABELS: Record<ReleaseQaStatus, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "Testing",
  PASSED: "Passed",
  FAILED: "Failed",
  BLOCKED: "Blocked",
};

export const QA_STATUS_TONE: Record<ReleaseQaStatus, Tone> = {
  NOT_STARTED: "gray",
  IN_PROGRESS: "blue",
  PASSED: "green",
  FAILED: "red",
  /* Amber rather than red: blocked is "we could not test this", which is a different problem
     from "we tested it and it is broken", and needs a different person to fix it. */
  BLOCKED: "amber",
};

export const TEST_STATUS_LABELS: Record<ReleaseTestStatus, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "In progress",
  PASSED: "Signed off",
  PASSED_WITH_WAIVER: "Signed off with waiver",
  FAILED: "Not approved",
};

export const PREREQUISITE_STATUS_LABELS: Record<ReleasePrerequisiteStatus, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "In progress",
  COMPLETED: "Complete",
  FAILED: "Failed",
  BLOCKED: "Blocked",
  SKIPPED: "Skipped",
};

export const PREREQUISITE_STATUS_TONE: Record<ReleasePrerequisiteStatus, Tone> = {
  NOT_STARTED: "gray",
  IN_PROGRESS: "blue",
  COMPLETED: "green",
  FAILED: "red",
  BLOCKED: "amber",
  SKIPPED: "gray",
};

/**
 * §14's coded reasons, in the order the drawers offer them.
 *
 * <p>Ordered by how often each is the real answer rather than alphabetically, so the common case
 * is near the top and nobody reaches for "Something else" because the right code was buried
 * twelve rows down.
 */
export const SCOPE_CHANGE_REASONS: [ReleaseScopeChangeReason, string][] = [
  ["SCOPE_CHANGE", "The scope changed"],
  ["QA_FAILURE", "Testing failed"],
  ["DEVELOPMENT_INCOMPLETE", "The work was not finished"],
  ["DEPENDENCY_DELAY", "Waiting on a dependency"],
  ["PRIORITY_CHANGE", "Priorities changed"],
  ["CUSTOMER_REQUEST", "The customer asked for it"],
  ["TECHNICAL_RISK", "Too risky to include"],
  ["BUILD_FAILURE", "The build failed"],
  ["SECURITY_ISSUE", "A security issue"],
  ["PRODUCTION_RISK", "Too risky for production"],
  ["RESOURCE_CONSTRAINT", "Nobody available"],
  ["HOTFIX", "An urgent fix"],
  ["BACKPORT", "Backported from a later release"],
  ["OTHER", "Something else"],
];

/** Mirrors ReleaseScopeChangeReason.requiresComment() — the server refuses these without one. */
export const REASON_REQUIRING_COMMENT: ReleaseScopeChangeReason[] = ["OTHER"];

/**
 * How far through the scope a release is.
 *
 * <p>Deliberately returns the counts as well as the fraction. §20's objection to a bare percentage
 * applies here too: "92%" invites the question "of how many?", and the answer should already be
 * on screen.
 */
export function describeScope(complete: number, total: number): string {
  if (total === 0) {
    return "No work in this release yet";
  }
  return `${complete} of ${total} complete`;
}

export function describeQa(passed: number, required: number): string {
  if (required === 0) {
    return "Nothing needs testing";
  }
  return `${passed} of ${required} passed`;
}

/**
 * A release version, always as the team wrote it.
 *
 * <p>Never parsed, compared or reformatted (§6). "2.1", "R2026.08" and "August-Production" are
 * all things real teams ship, and code that assumes three dot-separated integers breaks on the
 * second customer.
 */
export function formatVersion(version: string): string {
  return version;
}

/** A planned instant, in the zone the release was agreed in rather than the reader's. */
export function formatReleaseMoment(iso: string | null, timeZone: string): string {
  if (!iso) {
    return "Not scheduled";
  }
  const formatted = new Intl.DateTimeFormat(undefined, {
    timeZone,
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(iso));
  return `${formatted} ${shortZone(iso, timeZone)}`;
}

/** "IST", "GMT+1" — whatever the platform calls the zone at that moment. */
function shortZone(iso: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", { timeZone, timeZoneName: "short" })
    .formatToParts(new Date(iso));
  return parts.find((part) => part.type === "timeZoneName")?.value ?? "";
}

export function formatTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}


// ── Execution phase vocabulary (§31-§41) ────────────────────────────────────────────────────

export const APPROVAL_STATUS_LABELS: Record<ReleaseApprovalStatus, string> = {
  WAITING: "Waiting",
  APPROVED: "Approved",
  REJECTED: "Refused",
};

/**
 * Amber for waiting, red for refused.
 *
 * <p>The two mean different things and call for different actions — waiting means chase somebody,
 * refused means fix something — so they must never look alike.
 */
export const APPROVAL_STATUS_TONE: Record<ReleaseApprovalStatus, Tone> = {
  WAITING: "amber",
  APPROVED: "green",
  REJECTED: "red",
};

export const EXECUTION_PHASE_LABELS: Record<ExecutionPhase, string> = {
  PREPARE: "Prepare",
  DEPLOY: "Deploy",
  VERIFY: "Verify",
  FINALISE: "Finalise",
};

export const STEP_STATUS_LABELS: Record<ExecutionStepStatus, string> = {
  NOT_STARTED: "Not started",
  READY: "Ready",
  IN_PROGRESS: "Running",
  COMPLETED: "Done",
  FAILED: "Failed",
  BLOCKED: "Blocked",
  SKIPPED: "Skipped",
};

export const STEP_STATUS_TONE: Record<ExecutionStepStatus, Tone> = {
  NOT_STARTED: "gray",
  READY: "blue",
  IN_PROGRESS: "amber",
  COMPLETED: "green",
  FAILED: "red",
  BLOCKED: "amber",
  /* Grey, not green. A skipped step is not a done one, and colouring it as success would let a
     runbook of skipped steps read as a clean deployment. */
  SKIPPED: "gray",
};

/** What the plan says happens if this step fails (§38). Recorded, never performed. */
export const ON_FAILURE_LABELS: Record<OnFailureAction, string> = {
  CONTINUE: "Carry on",
  STOP_RELEASE: "Stop the release",
  TRIGGER_ROLLBACK: "Consider rolling back",
  MANUAL_DECISION: "Somebody decides",
};

export const ROLLBACK_STATUS_LABELS: Record<RollbackStepStatus, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "Running",
  COMPLETED: "Done",
  FAILED: "Failed",
  SKIPPED: "Skipped",
};

export const ROLLBACK_STATUS_TONE: Record<RollbackStepStatus, Tone> = {
  NOT_STARTED: "gray",
  IN_PROGRESS: "amber",
  COMPLETED: "green",
  FAILED: "red",
  SKIPPED: "gray",
};

export const VALIDATION_STATUS_LABELS: Record<ValidationStatus, string> = {
  NOT_STARTED: "Not started",
  IN_PROGRESS: "Checking",
  PASSED: "Pass",
  FAILED: "Fail",
  SKIPPED: "Skipped",
};

export const VALIDATION_STATUS_TONE: Record<ValidationStatus, Tone> = {
  NOT_STARTED: "gray",
  IN_PROGRESS: "blue",
  PASSED: "green",
  FAILED: "red",
  SKIPPED: "gray",
};

/** "8m", "1h 12m" — how long a step actually took. */
export function formatDuration(minutes: number | null): string {
  if (minutes == null) {
    return "—";
  }
  if (minutes < 60) {
    return `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/**
 * How much of §41's observation window is left.
 *
 * <p>Counts down to an instant the server supplied rather than from a duration, so two people
 * watching in different places see the same answer instead of two clocks that have drifted.
 */
export function describeObservation(
  completeAt: string | null,
  complete: boolean,
  now: number = Date.now(),
): string {
  if (completeAt == null) {
    return "No observation window";
  }
  if (complete) {
    return "Observation complete";
  }
  const remaining = Math.max(0, Math.ceil((new Date(completeAt).getTime() - now) / 60000));
  return `${formatDuration(remaining)} of observation left`;
}

// ── known issues (§30) ────────────────────────────────────────────────────────────────────

export const SEVERITY_LABELS: Record<BugSeverity, string> = {
  BLOCKER: "Blocker",
  CRITICAL: "Critical",
  MAJOR: "Major",
  MINOR: "Minor",
  TRIVIAL: "Trivial",
};

export const SEVERITY_TONE: Record<BugSeverity, Tone> = {
  BLOCKER: "red",
  CRITICAL: "red",
  MAJOR: "amber",
  MINOR: "gray",
  TRIVIAL: "gray",
};

/** The two severities the readiness gate refuses to pass unaccepted (§21). */
export function blocksRelease(severity: BugSeverity): boolean {
  return severity === "BLOCKER" || severity === "CRITICAL";
}

/**
 * What a known issue means for the release, in a sentence.
 *
 * <p>The distinction this has to carry is between "nobody has decided about this yet" and
 * "somebody decided to ship with it". Both are rows in the same list, and only the second is a
 * documented limitation rather than an open problem.
 */
export function describeKnownIssue(issue: {
  severity: BugSeverity;
  riskAccepted: boolean;
  acceptedBy: { fullName: string } | null;
}): string {
  if (issue.riskAccepted) {
    return issue.acceptedBy
      ? `Risk accepted by ${issue.acceptedBy.fullName}`
      : "Risk accepted";
  }
  return blocksRelease(issue.severity)
    ? "Holding the release until somebody accepts the risk"
    : "Recorded — does not hold the release";
}

// ── deployments (§62) ─────────────────────────────────────────────────────────────────────

export const DEPLOYMENT_STATUS_LABELS: Record<DeploymentStatus, string> = {
  PLANNED: "Planned",
  IN_PROGRESS: "In progress",
  SUCCEEDED: "Succeeded",
  FAILED: "Failed",
  ROLLED_BACK: "Rolled back",
  CANCELLED: "Cancelled",
};

export const DEPLOYMENT_STATUS_TONE: Record<DeploymentStatus, Tone> = {
  PLANNED: "gray",
  IN_PROGRESS: "blue",
  SUCCEEDED: "green",
  FAILED: "red",
  ROLLED_BACK: "amber",
  CANCELLED: "gray",
};

/**
 * "2nd attempt to Production" — a retry is a separate attempt, and saying so is the point.
 *
 * <p>A deployment history that read "Production: succeeded" for a release that took three goes
 * would answer the question it exists to answer dishonestly.
 */
export function describeAttempt(attemptNo: number, environmentName: string): string {
  const ordinal =
    attemptNo === 1 ? "1st" : attemptNo === 2 ? "2nd" : attemptNo === 3 ? "3rd" : `${attemptNo}th`;
  return `${ordinal} attempt to ${environmentName}`;
}

/**
 * Whether a release is over and its plan is part of the record.
 *
 * <p>The same three statuses the server treats as terminal. Worth having on the client so a
 * screen does not offer an action the server will refuse — an "add" button that returns a 409 is
 * worse than an absent one, because it reads as a fault rather than a rule.
 */
export function isFinished(status: ReleaseStatus): boolean {
  return status === "COMPLETED" || status === "CANCELLED" || status === "ARCHIVED";
}

/**
 * The sign-off roles, in the order they are normally asked for.
 *
 * <p>These are hats worn for one release, not identity roles — somebody who is QA lead on 2.1 is
 * not thereby QA lead on everything — so they are labelled here rather than drawn from the
 * permission system.
 */
export const APPROVAL_ROLES: ReleaseApprovalRole[] = [
  "QA_LEAD",
  "TECHNICAL_LEAD",
  "PROJECT_MANAGER",
  "RELEASE_MANAGER",
  "DEVOPS",
  "SECURITY",
  "BUSINESS_OWNER",
  "CUSTOMER_UAT",
  "CHANGE_MANAGER",
];

export const APPROVAL_ROLE_LABELS: Record<ReleaseApprovalRole, string> = {
  QA_LEAD: "QA lead",
  TECHNICAL_LEAD: "Technical lead",
  PROJECT_MANAGER: "Project manager",
  RELEASE_MANAGER: "Release manager",
  DEVOPS: "DevOps",
  SECURITY: "Security",
  BUSINESS_OWNER: "Business owner",
  CUSTOMER_UAT: "Customer UAT",
  CHANGE_MANAGER: "Change manager",
};
